import { Platform } from 'react-native';
import Sound from 'react-native-sound';
import { callAudioSessionBusy } from '../calls/callAudioSession';

import { SOUNDS_ENABLED_BY_DEFAULT, readSoundsEnabled } from './soundPrefStorage';

/**
 * Общие правила звука приложения: включён ли он вообще, в какой аудиосессии играть каждую
 * роль и как часто разрешено звенеть входящим сообщениям.
 *
 * <p>Раньше каждый проигрыватель решал это сам, и решения разошлись: сообщение играло на
 * полной громкости в категории `Playback`, входящий — тоже `Playback` на громкости 1.0,
 * гудок — 0.75, служебный тон — 0.35. Здесь одно место на все четыре роли.
 */

// ---------------------------------------------------------------------------
// Переключатель «Звуки» из настроек
// ---------------------------------------------------------------------------

/**
 * Настройка читается с диска асинхронно, а решение «играть или нет» нужно синхронно —
 * в момент прихода сообщения по WS. Держим последнее известное значение в памяти, ровно как
 * это сделано у тактильной отдачи. Промахнуться можно один раз и только до `primeSoundsPref()`.
 */
let enabled = SOUNDS_ENABLED_BY_DEFAULT;

/** Прочитать сохранённую настройку в память. Вызывается один раз при старте приложения. */
export async function primeSoundsPref(): Promise<void> {
  try {
    enabled = await readSoundsEnabled();
  } catch {
    enabled = SOUNDS_ENABLED_BY_DEFAULT;
  }
}

/** Применить изменение немедленно, не дожидаясь записи на диск. */
export function setSoundsEnabled(next: boolean): void {
  enabled = next;
}

export function areSoundsEnabled(): boolean {
  return enabled;
}

/** Только для тестов. */
export function resetSoundPrefsForTests(): void {
  enabled = SOUNDS_ENABLED_BY_DEFAULT;
  lastMessageSoundAt = 0;
}

// ---------------------------------------------------------------------------
// Категории аудиосессии — как приложение уважает беззвучный режим телефона
// ---------------------------------------------------------------------------

type SoundRole = 'message' | 'ring' | 'tone';

/**
 * Категория аудиосессии для роли.
 *
 * <p><b>Здесь и живёт «режим без звука».</b> Отдельного определения системного режима не
 * нужно — достаточно попросить правильную сессию, дальше ОС всё делает сама:
 *
 * <ul>
 *   <li><b>iOS.</b> `Playback`, стоявший тут раньше, — это ровно та категория, которая
 *       <i>продолжает играть при выключенном звонке</i> боковым переключателем. Отсюда и
 *       берётся «телефон в беззвучном, а мессенджер звенит». `Ambient` и `SoloAmbient`
 *       переключатель уважают; разница между ними в том, глушить ли чужое аудио.
 *   <li><b>Android.</b> `react-native-sound` отображает категорию в поток AudioManager:
 *       `Playback` → `STREAM_MUSIC` (режимы «без звука» и «вибрация» его не глушат),
 *       `Ambient` → `STREAM_NOTIFICATION`, `Ring` → `STREAM_RING` — а вот эти два режим
 *       звонка телефона выключает штатно.
 * </ul>
 *
 * <p>Гудок исходящего в этот список не входит намеренно: он идёт по телефонному маршруту
 * (`Voice` / `PlayAndRecord` + `VoiceChat`), который выставляет сам проигрыватель гудка ради
 * разговорного динамика. Человек в этот момент держит телефон у уха и звонит сам — глушить
 * ему собственный вызов беззвучным режимом было бы неверно.
 */
export function audioCategoryFor(role: SoundRole): 'Ambient' | 'SoloAmbient' | 'Ring' {
  if (role === 'ring') {
    // Входящий звонок: на iOS перебиваем чужое аудио (SoloAmbient), на Android идём по
    // потоку звонка — там же, где живёт системный рингтон.
    return Platform.OS === 'ios' ? 'SoloAmbient' : 'Ring';
  }
  // Сообщение и служебные тоны — уведомительный поток, чужое аудио не трогаем.
  return 'Ambient';
}

/**
 * Выставить аудиосессию под роль. Звать <b>до</b> `new Sound(...)`: категория —
 * глобальное состояние модуля, и нативная сторона читает его в момент подготовки
 * проигрывателя, а не воспроизведения.
 *
 * <p>Приведение типа нужно из-за `'Ring'`: типы `react-native-sound` описывают только
 * категории AVAudioSession, а `'Ring'` — андроидная, нативный модуль отображает её в
 * `STREAM_RING`. Тем же приёмом в проигрывателе гудка передаётся `'Voice'`.
 */
export function applyAudioCategory(role: SoundRole): void {
  /**
   * Идёт разговор — категорию не трогаем.
   *
   * <p>На iOS `setCategory` бьёт по общей `AVAudioSession`, той самой, на
   * которой живёт звонок. Служебная отбивка «соединились» звучит ровно в момент
   * перехода в разговор и просила `Ambient`: категорию без входа и без
   * разговорного динамика. Маршрут живого звонка после неё уезжал в громкий
   * динамик — то самое «у второго абонента звук во всю громкость». То же самое
   * делал звук входящего сообщения, пришедшего посреди разговора.
   *
   * <p>Сам звук при этом не пропадает: `PlayAndRecord` играет, просто по
   * телефонному маршруту — в том динамике, к которому человек и так прижал ухо.
   *
   * <p>Android не затронут: там категория выбирает поток для следующего
   * проигрывателя (`STREAM_NOTIFICATION` / `STREAM_RING`) и на маршрут
   * разговора не влияет — а отбивка в разговоре по уведомительному потоку
   * как раз уместна.
   */
  if (Platform.OS === 'ios' && callAudioSessionBusy()) {
    return;
  }
  const setCategory = Sound.setCategory as unknown as (category: string, mix?: boolean) => void;
  setCategory(audioCategoryFor(role));
}

// ---------------------------------------------------------------------------
// Громкости
// ---------------------------------------------------------------------------

/**
 * Громкости ролей относительно системной. Сами файлы уже нормализованы генератором
 * (`scripts/gen-theme-sounds.mjs`) по единой таблице уровней, поэтому здесь остаётся только
 * соотношение между ролями, а не компенсация разброса между темами.
 */
export const SOUND_VOLUME = {
  message: 0.7,
  /** Входящий — единственный звук, которому положено дотягиваться из другой комнаты. */
  ring: 1.0,
  ringback: 0.75,
  tone: 0.6,
} as const;

/**
 * Плавное нарастание рингтона в начале звонка, мс.
 *
 * <p>Так звонят и iOS, и Pixel: первые полторы секунды тише, дальше на полную. Смысл не в
 * красоте — телефон рядом с человеком не должен бить с первой ноты, а телефон в сумке всё
 * равно дозвонится со второго цикла. Прошлый рингтон стартовал сразу на 1.0.
 */
export const RING_FADE_IN_MS = 1400;

// ---------------------------------------------------------------------------
// Ограничение частоты звука входящих сообщений
// ---------------------------------------------------------------------------

/**
 * Минимальный интервал между звуками сообщений, мс.
 *
 * <p>Без него активная группа превращалась в очередь накладывающихся «дзынь»: звук играл на
 * <i>каждое</i> входящее, без всякого ограничения, и десять сообщений подряд давали десять
 * одновременных проигрывателей. Две секунды — тот же порядок, что у Telegram и WhatsApp:
 * первое сообщение пачки слышно, остальные приходят молча.
 */
export const MESSAGE_SOUND_MIN_INTERVAL_MS = 2000;

let lastMessageSoundAt = 0;

/**
 * Разрешить звук сообщения и сразу же занять окно.
 *
 * <p>Проверка и отметка — одним вызовом намеренно: они обязаны быть неделимыми, иначе два
 * сообщения из одного пакета WS успеют проскочить между «спросил» и «отметил».
 */
export function claimMessageSoundSlot(now: number = Date.now()): boolean {
  if (now - lastMessageSoundAt < MESSAGE_SOUND_MIN_INTERVAL_MS) return false;
  lastMessageSoundAt = now;
  return true;
}

/**
 * Дождаться, пока нативный звук действительно остановится и освободит сессию.
 *
 * <p>Дело не в аккуратности, а в падении: если вернуться из остановки раньше, чем
 * react-native-sound закончил teardown, то следующий за ней `getUserMedia` (принять звонок,
 * начать исходящий) открывает микрофон поверх ещё живой AVAudioSession — и аудиодвижок
 * LiveKit-форка WebRTC на iOS падает.
 *
 * <p>`settled` нужен потому, что колбэк `stop` на некоторых сборках не приходит вовсе: тогда
 * освобождаем сами, в ветке `catch`, и обещание всё равно должно завершиться ровно один раз.
 *
 * <p>Было двумя копиями — у звонка входящего и у гудка исходящего; отличались они только
 * комментарием, хотя условие у обеих одно.
 */
export function stopAndReleaseNativeSound(s: {
  stop: (cb?: () => void) => void;
  release: () => void;
}): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      try {
        s.release();
      } catch {
        /* noop */
      }
      resolve();
    };
    try {
      s.stop(done);
    } catch {
      done();
    }
  });
}
